import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { API_BASE, apiFetch } from '../../lib/api';
import Spinner from '../Spinner';

interface ArtefactCardProps {
  artefactId: string;
  projectId: string;
  title: string;
  version: string;
  type: string;
  status: string;
  onSendMessage: (message: string) => void;
  onStatusChange?: (newStatus: string) => void;
  onNavigate?: (path: string) => void;
  isStale?: boolean;
  stalenessReason?: string;
  // #3175 (REGSYNC.D) follow-on, criterion 2 — explicit "resynced from the
  // register" indicator for SAD/ADR, backed by artefact_resync_markers
  // (migration 0245, side table). Distinct from isStale/stalenessReason
  // above: a SAD/ADR's own risk/open-question citations are AUTO-resynced
  // by the register-splice mechanism (AUTO_RESYNC_COVERAGE,
  // stalenessService.ts) rather than ever flagged stale, so without this
  // the founder would see a silent diff between sessions with no signal at
  // all that anything changed. Present only when the artefact has actually
  // been resynced at least once (null until then).
  lastResyncedAt?: string | null;
  resyncReason?: string | null;
  // UX standalone sessions (UX.S1) run after Phase 2.5 has already shipped, so
  // there is no phase transition to perform. When true the design_tokens
  // approved branch substitutes "View in Artefacts" for the handoff pill.
  isStandalone?: boolean;
  // View Prototype affordance (state-aware — NONE/CURRENT/STALE), only
  // rendered under type === 'prd'. Distinct from isStale/stalenessReason
  // above (PRD-approval staleness) — prototypeStale means the generated
  // prototype is behind the latest saved PRD, per checkPrototypeVersionParity.
  prototypeExists?: boolean;
  prototypeArtefactId?: string | null;
  prototypeStale?: boolean;
  // Fired after a successful Regenerate so the parent can re-fetch
  // prototype-status and flip the stale flag back to current — mirrors
  // onStatusChange's role for handleApprove.
  onPrototypeRegenerate?: () => void;
  // #2541 — Open Questions affordance, parity with View Prototype: absent
  // entirely (not disabled) when 0/undefined, same NONE-is-absent convention
  // as prototypeExists above. The count comes from countUnresolvedOpenQuestions
  // (openQuestionsService.ts), the SAME predicate the Phase-1 Exit Gate blocks
  // on — this card must never invent a different "unresolved" definition.
  unresolvedOpenQuestionsCount?: number;
  // #2544 — Risk-disposition affordance, exact parity with the Open
  // Questions badge above: absent entirely at 0/undefined, present with a
  // count badge otherwise. The count comes from countUndisposedRisks
  // (riskDispositionService.ts), the SAME predicate the Phase-1 Exit Gate
  // blocks on (isRiskFullyDisposed via hasUndisposedRisks) — this card must
  // never invent a different "undisposed" definition.
  undisposedRisksCount?: number;
  // #2612 — NFR-disposition affordance, exact parity with the Risk-
  // disposition badge above: absent entirely at 0/undefined, present with a
  // count badge otherwise. The count comes from countUndisposedNfrs
  // (nfrDispositionService.ts), the SAME predicate the Phase-1 Exit Gate
  // blocks on — this card must never invent a different "undisposed"
  // definition.
  undisposedNfrsCount?: number;
}

type ButtonVariant = 'primary' | 'outline' | 'nav' | 'approved' | 'nav-stale' | 'nav-warning';

interface ActionButton {
  label: React.ReactNode;
  variant: ButtonVariant;
  onClick: () => void;
  disabled?: boolean;
}

export default function ArtefactCard({
  artefactId,
  projectId,
  title,
  version,
  type,
  status: initialStatus,
  onSendMessage,
  onStatusChange,
  onNavigate,
  onPrototypeRegenerate,
  isStale = false,
  stalenessReason,
  lastResyncedAt = null,
  resyncReason = null,
  isStandalone = false,
  prototypeExists = false,
  prototypeArtefactId = null,
  prototypeStale = false,
  unresolvedOpenQuestionsCount = 0,
  undisposedRisksCount = 0,
  undisposedNfrsCount = 0,
}: ArtefactCardProps) {
  const typeLabel = type.replace(/_/g, ' ').toUpperCase();
  const [exporting, setExporting] = useState(false);
  const [localStatus, setLocalStatus] = useState(initialStatus);
  const [approving, setApproving] = useState(false);
  const [approveError, setApproveError] = useState('');
  // #2306 Part B — explicit Regenerate Prototype action, independent of
  // approval status (unlike handleApprove, this is available whether or
  // not the PRD is approved — regenerate is no longer tied to any PRD
  // lifecycle gate, it just rebuilds from whatever PRD content was last
  // saved).
  const [regenerating, setRegenerating] = useState(false);
  const [regenerateError, setRegenerateError] = useState('');
  // Server-side auto-approvals (UX Agent) flip the parent's status prop
  // without going through handleApprove, so localStatus needs to follow
  // prop changes — otherwise the handoff pill / next-step button never
  // appears until reload. Local handleApprove keeps working because it
  // sets both the prop (via onStatusChange) and localStatus directly,
  // which collapses to the same value.
  useEffect(() => {
    setLocalStatus(initialStatus);
  }, [initialStatus]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('blueprint_token');
      const res = await fetch(`${API_BASE}/artefacts/${artefactId}/export`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title}.docx`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const handleApprove = async () => {
    setApproving(true);
    setApproveError('');
    try {
      await apiFetch(`/artefacts/${artefactId}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status: 'approved' }),
      });
      setLocalStatus('approved');
      onStatusChange?.('approved');
    } catch (err) {
      setApproveError(err instanceof Error ? err.message : 'Failed to approve');
    } finally {
      setApproving(false);
    }
  };

  // #2306 Part B — the route (POST /artefacts/:id/regenerate-prototype)
  // pre-checks opt-in and in-flight status server-side and returns a clear
  // error for both (400 not-opted-in, 409 already-in-progress) rather than
  // the old auto-save path's silent no-op — surfaced here the same way
  // approveError already is, no separate proactive opt-in fetch needed.
  const handleRegenerate = async () => {
    setRegenerating(true);
    setRegenerateError('');
    try {
      await apiFetch(`/artefacts/${artefactId}/regenerate-prototype`, { method: 'POST' });
      // A successful regenerate is exactly the event that can flip
      // prototypeStale back to current — refresh the parent's read of it
      // rather than leaving the View Prototype badge stuck stale until reload.
      onPrototypeRegenerate?.();
    } catch (err) {
      setRegenerateError(err instanceof Error ? err.message : 'Failed to regenerate prototype');
    } finally {
      setRegenerating(false);
    }
  };

  const getActions = (): ActionButton[] => {
    if (localStatus === 'handed_off') return [];

    const isApproved = localStatus === 'approved';
    const approvedBtn: ActionButton = isStale
      ? { label: '⚠ Approved (Stale)', variant: 'approved', onClick: () => {}, disabled: true }
      : { label: '✓ Approved', variant: 'approved', onClick: () => {}, disabled: true };

    const reviewImpactBtn: ActionButton = {
      label: 'Review Impact',
      variant: 'outline',
      onClick: () => onSendMessage(`Review the impact of upstream amendments on the ${type.replace(/_/g, ' ')}`),
    };

    // #2306 Part B — regenerateBtn is deliberately available on BOTH the
    // approved and not-approved return arrays below: regeneration is no
    // longer tied to any PRD lifecycle gate (Approve PRD is unrelated to
    // the prototype, per #2306's corrected premise), so it must not be
    // hidden behind one.
    const regenerateBtn: ActionButton = {
      label: regenerating ? <><Spinner /> Regenerating…</> : 'Regenerate Prototype',
      variant: 'outline',
      onClick: () => void handleRegenerate(),
      disabled: regenerating,
    };

    // View Prototype — state-aware (NONE/CURRENT/STALE). NONE: no prototype
    // has ever been generated for this project, so the affordance is absent
    // entirely (viewPrototypeBtn stays null) rather than a disabled button —
    // a bare, always-present link here would recreate the exact staleness
    // confusion #2306 Part C fixed. CURRENT vs STALE is carried by the
    // 'nav-stale' variant, which reuses the same var(--warning) token as the
    // card's isStale badge above, without conflating the two: isStale is
    // PRD-approval staleness, prototypeStale is prototype-generation
    // currency (checkPrototypeVersionParity) — different questions.
    const viewPrototypeBtn: ActionButton | null = prototypeExists
      ? {
          label: prototypeStale ? '⚠ View Prototype (Stale)' : 'View Prototype',
          variant: prototypeStale ? 'nav-stale' : 'nav',
          onClick: () => onNavigate?.(`/artefacts?tabs=${prototypeArtefactId}&active=${prototypeArtefactId}`),
        }
      : null;

    // #2541 — Open Questions affordance, PRD-card only (parity with View
    // Prototype above): absent entirely at 0, present with a count badge
    // otherwise. Navigates to the project's OQ page, same target the Exit
    // Gate's own remediation link points at (phase-gate-agent.ts).
    const openQuestionsBtn: ActionButton | null = unresolvedOpenQuestionsCount > 0
      ? {
          label: `Open Questions (${unresolvedOpenQuestionsCount})`,
          variant: 'nav-warning',
          onClick: () => onNavigate?.(`/projects/${projectId}/open-questions`),
        }
      : null;

    // #2544 — Risk disposition affordance, same shape as Open Questions
    // above. Navigates to the project's Risk Register, the same target the
    // Exit Gate's own remediation link points at (phase-gate-agent.ts).
    const undisposedRisksBtn: ActionButton | null = undisposedRisksCount > 0
      ? {
          label: `Risks (${undisposedRisksCount})`,
          variant: 'nav-warning',
          onClick: () => onNavigate?.(`/projects/${projectId}/risks`),
        }
      : null;

    // #2612 — NFR disposition affordance, same shape as Risks/Open Questions
    // above. Navigates to the project's NFR page, same target the Exit
    // Gate's own remediation link points at (phase-gate-agent.ts).
    const undisposedNfrsBtn: ActionButton | null = undisposedNfrsCount > 0
      ? {
          label: `NFRs (${undisposedNfrsCount})`,
          variant: 'nav-warning',
          onClick: () => onNavigate?.(`/projects/${projectId}/nfrs`),
        }
      : null;

    if (type === 'prd') {
      if (!isApproved) {
        return [
          {
            label: approving ? <><Spinner /> Approving…</> : 'Approve PRD',
            variant: 'primary',
            onClick: () => void handleApprove(),
            disabled: approving,
          },
          {
            label: 'Edit PRD',
            variant: 'outline',
            onClick: () => onSendMessage("I'd like to make changes to the PRD"),
          },
          regenerateBtn,
          ...(viewPrototypeBtn ? [viewPrototypeBtn] : []),
          ...(openQuestionsBtn ? [openQuestionsBtn] : []),
          ...(undisposedRisksBtn ? [undisposedRisksBtn] : []),
          ...(undisposedNfrsBtn ? [undisposedNfrsBtn] : []),
        ];
      }
      return [
        approvedBtn,
        ...(isStale ? [reviewImpactBtn] : []),
        {
          label: 'Generate User Stories',
          variant: 'outline' as ButtonVariant,
          onClick: () => onSendMessage('Generate user stories from the approved PRD'),
        },
        regenerateBtn,
        ...(viewPrototypeBtn ? [viewPrototypeBtn] : []),
        ...(openQuestionsBtn ? [openQuestionsBtn] : []),
        ...(undisposedRisksBtn ? [undisposedRisksBtn] : []),
        ...(undisposedNfrsBtn ? [undisposedNfrsBtn] : []),
      ];
    }

    if (type === 'user_stories') {
      if (!isApproved) {
        return [
          {
            label: approving ? <><Spinner /> Approving…</> : 'Approve Stories',
            variant: 'primary',
            onClick: () => void handleApprove(),
            disabled: approving,
          },
          {
            label: 'Edit Stories',
            variant: 'outline',
            onClick: () => onSendMessage("I'd like to make changes to the user stories"),
          },
        ];
      }
      return [
        approvedBtn,
        ...(isStale ? [reviewImpactBtn] : []),
        {
          label: 'Hand Off to Architecture →',
          variant: 'nav' as ButtonVariant,
          onClick: () => onNavigate?.(`/projects/${projectId}`),
        },
      ];
    }

    if (type === 'sad') {
      const generateADRs: ActionButton = {
        label: 'Generate ADRs',
        variant: 'outline',
        onClick: () => onSendMessage('Generate the Architectural Decision Records'),
      };
      if (!isApproved) {
        return [
          {
            label: approving ? <><Spinner /> Approving…</> : 'Approve SAD',
            variant: 'primary',
            onClick: () => void handleApprove(),
            disabled: approving,
          },
          {
            label: 'Edit SAD',
            variant: 'outline',
            onClick: () => onSendMessage("I'd like to make changes to the SAD"),
          },
          generateADRs,
        ];
      }
      return [approvedBtn, ...(isStale ? [reviewImpactBtn] : []), generateADRs];
    }

    if (type === 'adr') {
      if (!isApproved) {
        return [
          {
            label: approving ? <><Spinner /> Approving…</> : 'Approve ADRs',
            variant: 'primary',
            onClick: () => void handleApprove(),
            disabled: approving,
          },
          {
            label: 'Edit ADRs',
            variant: 'outline',
            onClick: () => onSendMessage("I'd like to make changes to the ADRs"),
          },
        ];
      }
      return [
        approvedBtn,
        ...(isStale ? [reviewImpactBtn] : []),
        {
          // #3168 — Sprint 11 UX.A1 inserted Phase 2.5 (UX Design) between
          // Architecture and Sprint Planning in PHASE_ORDER
          // (autonomous-transition.service.ts); this label was never
          // updated to match. ProjectDashboardPage.tsx's own
          // Architecture-phase handoff button already says this correctly.
          // Navigation is unchanged — the dashboard itself resolves the
          // actual next phase.
          label: 'Hand Off to UX Design →',
          variant: 'nav' as ButtonVariant,
          onClick: () => onNavigate?.(`/projects/${projectId}`),
        },
      ];
    }

    if (type === 'sprint_plan') {
      if (!isApproved) {
        return [
          {
            label: approving ? <><Spinner /> Approving…</> : 'Approve Sprint Plan',
            variant: 'primary',
            onClick: () => void handleApprove(),
            disabled: approving,
          },
          {
            label: 'Edit Sprint Plan',
            variant: 'outline',
            onClick: () => onSendMessage("I'd like to make changes to the Sprint Plan"),
          },
          {
            label: 'Generate Backlog',
            variant: 'outline',
            onClick: () => onSendMessage('Generate the structured sprint backlog JSON'),
          },
        ];
      }
      return [
        approvedBtn,
        {
          label: 'Generate Backlog',
          variant: 'outline',
          onClick: () => onSendMessage('Generate the structured sprint backlog JSON'),
        },
        {
          label: 'Hand Off to Development →',
          variant: 'nav',
          onClick: () => onNavigate?.(`/projects/${projectId}`),
        },
      ];
    }

    if (type === 'sprint_backlog') {
      if (!isApproved) {
        return [
          {
            label: approving ? <><Spinner /> Approving…</> : 'Approve Backlog',
            variant: 'primary',
            onClick: () => void handleApprove(),
            disabled: approving,
          },
          {
            label: 'Edit Backlog',
            variant: 'outline',
            onClick: () => onSendMessage("I'd like to make changes to the sprint backlog"),
          },
        ];
      }
      return [
        approvedBtn,
        {
          label: 'Hand Off to Development →',
          variant: 'nav',
          onClick: () => onNavigate?.(`/projects/${projectId}`),
        },
      ];
    }

    if (type === 'user_journey_map') {
      if (!isApproved) {
        return [
          {
            label: approving ? <><Spinner /> Approving…</> : 'Approve Journey Map',
            variant: 'primary',
            onClick: () => void handleApprove(),
            disabled: approving,
          },
          {
            label: 'Edit Journey Map',
            variant: 'outline',
            onClick: () => onSendMessage("I'd like to make changes to the user journey map"),
          },
        ];
      }
      return [
        approvedBtn,
        ...(isStale ? [reviewImpactBtn] : []),
        {
          label: 'Extract Screen Inventory',
          variant: 'outline',
          onClick: () => onSendMessage('Extract the screen inventory from the approved journey map'),
        },
      ];
    }

    if (type === 'screen_inventory') {
      if (!isApproved) {
        return [
          {
            label: approving ? <><Spinner /> Approving…</> : 'Approve Screen Inventory',
            variant: 'primary',
            onClick: () => void handleApprove(),
            disabled: approving,
          },
          {
            label: 'Edit Screen Inventory',
            variant: 'outline',
            onClick: () => onSendMessage("I'd like to make changes to the screen inventory"),
          },
        ];
      }
      return [
        approvedBtn,
        ...(isStale ? [reviewImpactBtn] : []),
        {
          label: 'Generate Screen Specs',
          variant: 'outline',
          onClick: () => onSendMessage('Generate the screen specifications for each screen'),
        },
      ];
    }

    if (type === 'screen_spec') {
      if (!isApproved) {
        return [
          {
            label: approving ? <><Spinner /> Approving…</> : 'Approve Screen Specs',
            variant: 'primary',
            onClick: () => void handleApprove(),
            disabled: approving,
          },
          {
            label: 'Edit Screen Specs',
            variant: 'outline',
            onClick: () => onSendMessage("I'd like to make changes to the screen specifications"),
          },
        ];
      }
      return [
        approvedBtn,
        ...(isStale ? [reviewImpactBtn] : []),
        {
          label: 'Generate Component Catalogue',
          variant: 'outline',
          onClick: () => onSendMessage('Generate the component catalogue from the approved screen specifications'),
        },
      ];
    }

    if (type === 'component_catalogue') {
      if (!isApproved) {
        return [
          {
            label: approving ? <><Spinner /> Approving…</> : 'Approve Catalogue',
            variant: 'primary',
            onClick: () => void handleApprove(),
            disabled: approving,
          },
          {
            label: 'Edit Catalogue',
            variant: 'outline',
            onClick: () => onSendMessage("I'd like to make changes to the component catalogue"),
          },
        ];
      }
      return [
        approvedBtn,
        ...(isStale ? [reviewImpactBtn] : []),
        {
          label: 'Generate Design Tokens',
          variant: 'outline',
          onClick: () => onSendMessage('Generate the design tokens from the approved component catalogue'),
        },
      ];
    }

    if (type === 'design_tokens') {
      if (!isApproved) {
        return [
          {
            label: approving ? <><Spinner /> Approving…</> : 'Approve Design Tokens',
            variant: 'primary',
            onClick: () => void handleApprove(),
            disabled: approving,
          },
          {
            label: 'Edit Design Tokens',
            variant: 'outline',
            onClick: () => onSendMessage("I'd like to make changes to the design tokens"),
          },
        ];
      }
      // design_tokens is the final Phase 2.5 artefact. In a gated session the
      // approved tokens unlock the Phase 2.5 → 3 handoff (the Phase 2.5
      // handoff API requires user_journey_map + screen_inventory; tokens
      // imply both because Stage 8 only fires after the earlier stages have
      // passed). Standalone sessions (UX.S1) run after the project is
      // already past Phase 2.5 — no phase transition, so we point the user
      // at the artefacts page instead.
      const finalAction: ActionButton = isStandalone
        ? {
            label: 'View in Artefacts →',
            variant: 'nav',
            onClick: () => onNavigate?.(`/artefacts?tabs=${artefactId}&active=${artefactId}`),
          }
        : {
            label: 'Hand Off to Sprint Planning →',
            variant: 'nav',
            onClick: () => onNavigate?.(`/projects/${projectId}`),
          };
      return [approvedBtn, ...(isStale ? [reviewImpactBtn] : []), finalAction];
    }

    if (type === 'change_order') {
      return [
        {
          label: 'View Change Order',
          variant: 'outline',
          onClick: () => onNavigate?.(`/projects/${projectId}/change-orders`),
        },
      ];
    }

    return [];
  };

  const buttonClass = (variant: ButtonVariant, disabled?: boolean): string => {
    const base = 'text-xs font-medium px-3 py-1 rounded-full transition-colors whitespace-nowrap';
    if (variant === 'approved') return `${base} cursor-default`;
    if (disabled) return `${base} opacity-50 cursor-not-allowed`;
    if (variant === 'primary') return `${base} text-white`;
    if (variant === 'nav') return `${base}`;
    return `${base}`;
  };

  const actions = getActions();

  return (
    <div className="mx-4 mb-3 rounded-lg" style={{ border: '1px solid var(--border)', background: 'var(--surface)', boxShadow: 'var(--shadow-sm)', ...(isStale ? { borderTop: '3px solid var(--warning)' } : {}) }}>
      <div className="p-3 flex items-center justify-between gap-4">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="text-lg flex-shrink-0">📄</span>
          <div className="min-w-0">
            <p className="truncate" style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>
              {typeLabel} Created: {title}
            </p>
            <p style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-secondary)' }}>v{version}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Link
            to={`/artefacts?tabs=${artefactId}&active=${artefactId}`}
            className="text-xs font-medium hover:underline whitespace-nowrap"
            style={{ color: 'var(--primary)' }}
          >
            View Full Document
          </Link>
          <span className="text-gray-300">|</span>
          <button
            onClick={() => void handleExport()}
            disabled={exporting}
            className="text-xs font-medium hover:underline whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ color: 'var(--primary)' }}
          >
            {exporting ? 'Exporting…' : 'Export .docx'}
          </button>
        </div>
      </div>
      {isStale && (
        <div className="px-3 pb-1 flex items-center gap-1.5" style={{ fontSize: 11, color: 'var(--warning)', fontFamily: 'var(--font-body)' }}>
          <span>⚠</span>
          <span>{stalenessReason ?? 'Upstream artefact was amended — this document may need updating'}</span>
        </div>
      )}
      {/* #3175 (REGSYNC.D) follow-on — informational, not a warning: this
          content was automatically kept in sync with the register, not an
          action item. Suppressed while isStale is shown (an unrelated
          amendment-driven flag) so the card leads with the thing that
          actually needs attention; the resync marker itself doesn't clear
          just because another flag is active, so it reappears once that
          flag resolves. */}
      {!isStale && lastResyncedAt && (
        <div className="px-3 pb-1 flex items-center gap-1.5" style={{ fontSize: 11, color: 'var(--text-secondary)', fontFamily: 'var(--font-body)' }}>
          <span>🔄</span>
          <span>
            Resynced from the register {new Date(lastResyncedAt).toLocaleString()}
            {resyncReason ? ` — ${resyncReason}` : ''}
          </span>
        </div>
      )}
      {actions.length > 0 && (
        <div className="px-3 pb-3 flex items-center gap-2 flex-wrap">
          {actions.map((action, idx) => (
            <button
              key={idx}
              onClick={action.onClick}
              disabled={action.disabled}
              className={buttonClass(action.variant, action.disabled)}
              style={
                action.variant === 'approved'
                  ? isStale
                    ? { background: 'rgba(245,158,11,0.12)', color: 'var(--warning)' }
                    : { background: 'rgba(16,185,129,0.12)', color: 'var(--success)' }
                  : action.variant === 'primary'
                    ? { background: 'var(--primary)' }
                    : action.variant === 'nav'
                      ? { border: '1px solid var(--primary-light)', color: 'var(--primary)' }
                      : action.variant === 'nav-stale' || action.variant === 'nav-warning'
                        ? { border: '1px solid var(--warning)', color: 'var(--warning)' }
                        : { border: '1px solid var(--primary)', color: 'var(--primary)' }
              }
            >
              {action.label}
            </button>
          ))}
        </div>
      )}
      {approveError && (
        <p className="px-3 pb-2 text-xs text-red-500">{approveError}</p>
      )}
      {regenerateError && (
        <p className="px-3 pb-2 text-xs text-red-500">{regenerateError}</p>
      )}
    </div>
  );
}
